import { useEffect, useMemo, useState } from 'react';
import { Badge, Button, Card, Col, Form, Modal, Row } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dayjs from 'dayjs';
import SoftBadge from 'components/common/SoftBadge';
import LoadingContainer from 'components/common/LoadingContainer';
import CustomAdvanceTable from 'components/common/advance-table/CustomAdvanceTable';
import {
  FILTER_FIELD_STYLE,
  FormProvider,
  RHFSelect
} from 'components/common/form';
import { permissionsKeyMap } from 'routes/permissions/permissionKeys';
import { useMedicalForms, useReviewMedicalForm } from 'hooks/useMedicalForms';
import paths from 'routes/paths';

const yesNo = (flag, details) =>
  flag ? (
    <>
      <Badge bg="warning" text="dark" className="me-2">
        Yes
      </Badge>
      <span style={{ whiteSpace: 'pre-wrap' }}>{details}</span>
    </>
  ) : (
    <span className="text-600">No</span>
  );

const Field = ({ label, value }) => (
  <Col md={6} className="mb-2">
    <div className="fs--1 text-600">{label}</div>
    <div className="fw-medium" style={{ whiteSpace: 'pre-wrap' }}>
      {value || <span className="text-400">—</span>}
    </div>
  </Col>
);

const DetailModal = ({ form, onHide, onReview, isReviewing }) => {
  const [notes, setNotes] = useState(form?.admin_notes || '');
  if (!form) return null;
  return (
    <Modal show onHide={onHide} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title as="h5">
          {form.full_name}{' '}
          <SoftBadge
            bg={form.status === 'reviewed' ? 'success' : 'warning'}
            pill
            className="ms-2"
          >
            {form.status}
          </SoftBadge>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <h6 className="text-primary">Personal details</h6>
        <Row>
          <Field label="Full name" value={form.full_name} />
          <Field
            label="Date of birth"
            value={dayjs(form.date_of_birth).format('DD/MM/YYYY')}
          />
          <Field label="Address" value={form.address} />
          <Field
            label="Email / phone"
            value={[form.email, form.phone].filter(Boolean).join(' · ')}
          />
          <Field
            label="Country(ies) to be visited"
            value={form.countries_visited}
          />
          <Field
            label="Trip dates"
            value={`${dayjs(form.departure_date).format(
              'DD/MM/YYYY'
            )} → ${dayjs(form.return_date).format('DD/MM/YYYY')}`}
          />
        </Row>
        <h6 className="text-primary mt-3">Health / medical</h6>
        <Row>
          <Col md={12} className="mb-2">
            <div className="fs--1 text-600">Allergies</div>
            {yesNo(form.has_allergies, form.allergies_details)}
          </Col>
          <Col md={12} className="mb-2">
            <div className="fs--1 text-600">Existing medical conditions</div>
            {yesNo(form.has_conditions, form.conditions_details)}
          </Col>
          <Col md={12} className="mb-2">
            <div className="fs--1 text-600">Current medication</div>
            {yesNo(form.takes_medication, form.medication_details)}
          </Col>
          <Col md={12} className="mb-2">
            <div className="fs--1 text-600">Further information</div>
            {yesNo(form.has_further_info, form.further_info)}
          </Col>
        </Row>
        <h6 className="text-primary mt-3">Doctor</h6>
        <Row>
          <Field label="Name" value={form.gp_name} />
          <Field label="Telephone" value={form.gp_phone} />
          <Field label="Address" value={form.gp_address} />
        </Row>
        <h6 className="text-primary mt-3">Emergency contacts</h6>
        <Row>
          <Field
            label="Contact 1"
            value={`${form.ec1_name} (${form.ec1_relationship}) · ${
              form.ec1_phone
            }${form.ec1_address ? `\n${form.ec1_address}` : ''}`}
          />
          <Field
            label="Contact 2"
            value={`${form.ec2_name} (${form.ec2_relationship}) · ${
              form.ec2_phone
            }${form.ec2_address ? `\n${form.ec2_address}` : ''}`}
          />
        </Row>
        <h6 className="text-primary mt-3">Consent</h6>
        <p className="mb-1">
          Signed by <strong>{form.signature_name}</strong> on{' '}
          {dayjs(form.signed_on).format('DD/MM/YYYY')} · submitted{' '}
          {dayjs(form.created_at).format('DD/MM/YYYY HH:mm')}
        </p>
        {form.status === 'reviewed' && (
          <p className="text-600 fs--1 mb-1">
            Reviewed by{' '}
            {form.reviewed_by_user
              ? `${form.reviewed_by_user.first_name} ${form.reviewed_by_user.last_name}`
              : 'staff'}{' '}
            {form.reviewed_at &&
              dayjs(form.reviewed_at).format('DD/MM/YYYY HH:mm')}
          </p>
        )}
        <Form.Group className="mt-3">
          <Form.Label>Admin notes</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            value={notes}
            onChange={e => setNotes(e.target.value)}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="falcon-default" onClick={onHide}>
          Close
        </Button>
        {form.status === 'reviewed' ? (
          <Button
            variant="falcon-warning"
            disabled={isReviewing}
            onClick={() =>
              onReview({ id: form.id, status: 'new', adminNotes: notes })
            }
          >
            Mark as new
          </Button>
        ) : (
          <Button
            variant="success"
            disabled={isReviewing}
            onClick={() =>
              onReview({ id: form.id, status: 'reviewed', adminNotes: notes })
            }
          >
            <FontAwesomeIcon icon="check" className="me-1" /> Mark reviewed
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

const STATUS_OPTIONS = [
  { label: 'Awaiting review', value: 'new' },
  { label: 'Reviewed', value: 'reviewed' }
];
const FLAG_OPTIONS = [
  { label: 'Allergies', value: 'has_allergies' },
  { label: 'Medical conditions', value: 'has_conditions' },
  { label: 'Medication', value: 'takes_medication' },
  { label: 'Further info', value: 'has_further_info' }
];

const MedicalFormsAdmin = () => {
  const { forms, isLoading } = useMedicalForms();
  const { review, isReviewing } = useReviewMedicalForm();
  const [selected, setSelected] = useState(null);
  const [filters, setFilters] = useState({
    status: [],
    flags: [],
    country: []
  });

  const form = useForm({
    defaultValues: { status: [], flags: [], country: [] }
  });
  const watched = form.watch();
  const watchedKey = JSON.stringify(watched);
  useEffect(() => {
    setFilters(watched);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedKey]);

  const publicUrl = `${window.location.origin}${paths.medicalForm}`;

  const countryOptions = useMemo(
    () =>
      [
        ...new Set(
          forms
            .flatMap(f =>
              (f.countries_visited || '').split(',').map(c => c.trim())
            )
            .filter(Boolean)
        )
      ]
        .sort()
        .map(c => ({ label: c, value: c })),
    [forms]
  );

  const tableData = useMemo(
    () =>
      forms
        .filter(f => {
          if (filters.status?.length && !filters.status.includes(f.status))
            return false;
          if (
            filters.country?.length &&
            !(f.countries_visited || '')
              .split(',')
              .map(c => c.trim())
              .some(c => filters.country.includes(c))
          )
            return false;
          if (filters.flags?.length && !filters.flags.some(flag => f[flag]))
            return false;
          return true;
        })
        .map(f => ({
          ...f,
          trip_dates: `${dayjs(f.departure_date).format('DD/MM/YY')} – ${dayjs(
            f.return_date
          ).format('DD/MM/YY')}`
        })),
    [forms, filters]
  );

  const newCount = forms.filter(f => f.status === 'new').length;
  const hasActiveFilters = Object.values(filters).some(v => v?.length);

  const columns = [
    {
      accessor: 'full_name',
      Header: 'Name',
      cellProps: { className: 'py-2' },
      Cell: rowData => {
        const { full_name, email } = rowData.row.original;
        return (
          <Link
            to="#!"
            className="fw-medium text-1100 hover-primary"
            onClick={e => {
              e.preventDefault();
              setSelected(rowData.row.original);
            }}
          >
            {full_name}
            {email && <div className="text-600 fw-normal fs--1">{email}</div>}
          </Link>
        );
      }
    },
    {
      accessor: 'countries_visited',
      Header: 'Trip',
      cellProps: { className: 'py-2' }
    },
    {
      accessor: 'trip_dates',
      Header: 'Dates',
      cellProps: { className: 'py-2 text-nowrap' }
    },
    {
      accessor: 'flags',
      Header: 'Flags',
      disableSortBy: true,
      cellProps: { className: 'py-2' },
      Cell: rowData => {
        const f = rowData.row.original;
        const none =
          !f.has_allergies && !f.has_conditions && !f.takes_medication;
        return (
          <>
            {f.has_allergies && (
              <SoftBadge bg="warning" pill className="me-1">
                Allergies
              </SoftBadge>
            )}
            {f.has_conditions && (
              <SoftBadge bg="danger" pill className="me-1">
                Conditions
              </SoftBadge>
            )}
            {f.takes_medication && (
              <SoftBadge bg="info" pill className="me-1">
                Medication
              </SoftBadge>
            )}
            {none && <span className="text-500">None</span>}
          </>
        );
      }
    },
    {
      accessor: 'created_at',
      Header: 'Submitted',
      cellProps: { className: 'py-2 text-nowrap' },
      Cell: rowData =>
        dayjs(rowData.row.original.created_at).format('DD/MM/YY HH:mm')
    },
    {
      accessor: 'status',
      Header: 'Status',
      cellProps: { className: 'py-2' },
      Cell: rowData => {
        const { status } = rowData.row.original;
        return (
          <SoftBadge bg={status === 'reviewed' ? 'success' : 'warning'} pill>
            {status === 'reviewed' ? 'Reviewed' : 'New'}
          </SoftBadge>
        );
      }
    },
    {
      accessor: 'id_',
      Header: '',
      disableSortBy: true,
      cellProps: { className: 'py-2 text-end' },
      Cell: rowData => (
        <Button
          variant="falcon-default"
          size="sm"
          onClick={() => setSelected(rowData.row.original)}
        >
          View
        </Button>
      )
    }
  ];

  if (isLoading) return <LoadingContainer />;

  return (
    <>
      <Row className="mb-3">
        <Col>
          <Card className="shadow-none border">
            <Card.Body>
              <FormProvider methods={form}>
                <Row className="g-3 align-items-end">
                  <Col md={4} lg style={FILTER_FIELD_STYLE}>
                    <RHFSelect
                      name="status"
                      label="Status"
                      options={STATUS_OPTIONS}
                      noOptional
                      isClearable
                      isMulti
                    />
                  </Col>
                  <Col md={4} lg style={FILTER_FIELD_STYLE}>
                    <RHFSelect
                      name="flags"
                      label="Health flags"
                      options={FLAG_OPTIONS}
                      noOptional
                      isClearable
                      isMulti
                    />
                  </Col>
                  <Col md={4} lg style={FILTER_FIELD_STYLE}>
                    <RHFSelect
                      name="country"
                      label="Trip country"
                      options={countryOptions}
                      noOptional
                      isClearable
                      isMulti
                    />
                  </Col>
                  {hasActiveFilters && (
                    <Col md={4} lg style={FILTER_FIELD_STYLE}>
                      <Button
                        variant="outline-secondary"
                        className="w-100"
                        style={{ height: '38px' }}
                        onClick={() => form.reset()}
                      >
                        Clear Filters
                      </Button>
                    </Col>
                  )}
                </Row>
              </FormProvider>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col md={12} xxl={12} className="h-md-100">
          <CustomAdvanceTable
            data={tableData}
            columns={columns}
            title={`Medical Forms (${newCount} awaiting review)`}
            showSearchInput
            searchInputPlaceHolder="Search by name, email or country..."
            addNew
            addButtonLabel="Copy public form link"
            addButtonIcon="link"
            onClickAddNew={() => {
              navigator.clipboard?.writeText(publicUrl);
              toast.success('Public form link copied');
            }}
            subject={permissionsKeyMap.settings.children.users}
            loading={isLoading}
          />
        </Col>
      </Row>

      {selected && (
        <DetailModal
          key={selected.id}
          form={forms.find(f => f.id === selected.id) || selected}
          onHide={() => setSelected(null)}
          isReviewing={isReviewing}
          onReview={args =>
            review(args, { onSuccess: () => setSelected(null) })
          }
        />
      )}
    </>
  );
};

export default MedicalFormsAdmin;
